import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { useMergeRefs } from '@/js/libs/hooks/useMergeRefs/index.ts';
import { Button } from '../../Controls/Button/index.js';
import Icons, { Default as DefaultIcons } from '../../Elements/Icons/index.js';
import { Loader } from '../../Elements/Loader/index.js';
import { useNotification } from '../../Feedback/Notifications/index.js';
import { CopyTooltip } from '../../Feedback/Tooltip/CopyTooltip/index.js';
import { Tooltip } from '../../Feedback/Tooltip/index.js';
import { Input, InputPopover } from '../Input/index.js';
import { Textarea } from '../Textarea/index.js';
import { CreatableListItem, SuggestionsList } from './components/index.js';
import { PLACEHOLDERS, ALL_VALUE_SEPARATORS, DEFAULT_VALUE_SEPARATORS, SUGGESTIONS_LIMIT, SUGGESTIONS_MIN_QUERY_LENGTH, } from './constants.js';
import { containsSeparator, getLastFragment, splitBySeparators } from './helpers.js';
import { Wrapper, InputWrapper, ListWrapper, ListHeader, ListHeaderTitle, List, ListItem, ListFooter, Total, SortOrderWrapper, } from './styled.js';
import { CreatableListInputMode, SortOrder } from './types.js';
const replaceSpaces = (str) => str.replace(/\s+/g, '');
export const CreatableList = forwardRef(function CreatableList({ value = [], onChange, validateNewItem, formatOptionLabel, disabled = false, error = false, placeholders = PLACEHOLDERS, dataTestId, actions, suggestions, isLoading, allowSpacing = true, inputMode = CreatableListInputMode.INPUT, valueSeparator, applyIcon = 'Checkmark', }, ref) {
    const ApplyIcon = DefaultIcons[applyIcon];
    const { colors } = useTheme();
    const notify = useNotification();
    const [inputValue, setInputValue] = useState('');
    const [pastedInfo, setPastedInfo] = useState();
    const [appliedInfo, setAppliedInfo] = useState();
    const [sortOrder, setSortOrder] = useState(SortOrder.ASC);
    // Set when a suggestion is picked or a selected item is clicked, so the popover keeps suggesting for that
    // query after the textarea is cleared; typing releases it.
    const [lockedSuggestionsQuery, setLockedSuggestionsQuery] = useState();
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
    const [areSuggestionsDismissed, setAreSuggestionsDismissed] = useState(false);
    const textareaRef = React.useRef(null);
    const mergedTextareaRef = useMergeRefs(ref, textareaRef);
    const isEditMode = !!onChange;
    const isTextareaMode = inputMode === CreatableListInputMode.TEXTAREA;
    const hasValueSeparators = !!valueSeparator?.length;
    const usesSeparatorSplit = isTextareaMode || hasValueSeparators;
    const effectiveSeparators = valueSeparator?.length ? valueSeparator : isTextareaMode ? ALL_VALUE_SEPARATORS : DEFAULT_VALUE_SEPARATORS;
    const [t] = useTranslation(['backend', 'common']);
    const validateNewValue = useCallbackRef((newValue) => {
        if (newValue.trim().length === 0) {
            return placeholders.errorValueEmpty;
        }
        if (value.some((option) => option.label.toLowerCase() === newValue.toLowerCase())) {
            return placeholders.errorValueExists;
        }
        if (validateNewItem) {
            return validateNewItem(newValue);
        }
        return undefined;
    });
    const isValidItem = useCallbackRef((inputValue) => {
        const error = validateNewValue(inputValue);
        return !error;
    });
    const validationError = React.useMemo(() => (inputValue.length > 0 ? validateNewValue(inputValue) : undefined), [inputValue, validateNewValue]);
    const showCreatePreview = React.useMemo(() => {
        if (!isEditMode || isTextareaMode) {
            return false;
        }
        if (hasValueSeparators && containsSeparator(inputValue, effectiveSeparators)) {
            return false;
        }
        return isValidItem(inputValue);
    }, [effectiveSeparators, hasValueSeparators, inputValue, isEditMode, isTextareaMode, isValidItem]);
    const handleSortOrderChange = useCallbackRef(() => setSortOrder((current) => (current === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC)));
    const filteredAndSortedValues = React.useMemo(() => value
        .filter((option) => {
        if (!isTextareaMode && inputValue.length > 0) {
            const formattedLabel = formatOptionLabel?.(option);
            const label = typeof formattedLabel === 'string' ? formattedLabel : option.label;
            return label.toLowerCase().includes(inputValue.toLowerCase());
        }
        return true;
    })
        .sort((a, b) => {
        if (!formatOptionLabel) {
            return sortOrder === SortOrder.ASC ? a.label.localeCompare(b.label) : b.label.localeCompare(a.label);
        }
        const aLabel = formatOptionLabel(a)?.toString() ?? '';
        const bLabel = formatOptionLabel(b)?.toString() ?? '';
        return sortOrder === SortOrder.ASC ? aLabel.localeCompare(bLabel) : bLabel.localeCompare(aLabel);
    }), [formatOptionLabel, inputValue, isTextareaMode, sortOrder, value]);
    const hasSuggestions = isEditMode && isTextareaMode && !disabled && !!suggestions;
    const suggestionsQuery = (lockedSuggestionsQuery ?? getLastFragment(inputValue, effectiveSeparators)).toLowerCase();
    const isSuggestionsQueryLongEnough = suggestionsQuery.length >= SUGGESTIONS_MIN_QUERY_LENGTH;
    const requestedSuggestionsQuery = hasSuggestions && isSuggestionsQueryLongEnough ? suggestionsQuery : '';
    const isSuggestionsOpen = !!requestedSuggestionsQuery && !areSuggestionsDismissed;
    // Not `isValidItem`: a callback ref still holds the previous render's `value` while rendering, so a just-picked
    // suggestion would stay in the list for one more render.
    const visibleSuggestions = React.useMemo(() => {
        const selectedLabels = new Set(value.map((option) => option.label.toLowerCase()));
        return (suggestions?.options ?? [])
            .filter((option) => !selectedLabels.has(option.label.toLowerCase()) && !validateNewItem?.(option.label))
            .slice(0, SUGGESTIONS_LIMIT);
    }, [suggestions?.options, validateNewItem, value]);
    const activeSuggestionOptionIndex = Math.min(activeSuggestionIndex, Math.max(visibleSuggestions.length - 1, 0));
    const notifySuggestionsQueryChange = useCallbackRef((query) => suggestions?.onQueryChange(query));
    React.useEffect(() => {
        notifySuggestionsQueryChange(requestedSuggestionsQuery);
    }, [notifySuggestionsQueryChange, requestedSuggestionsQuery]);
    const formatSplitItem = useCallbackRef((item) => (allowSpacing ? item : replaceSpaces(item)));
    const applyItems = useCallbackRef((items) => {
        const splittedItems = items.map(formatSplitItem);
        const uniqueItems = Array.from(new Set(splittedItems));
        const newItems = uniqueItems.filter((item) => isValidItem(item)).map((item) => ({ label: item, value: item }));
        if (splittedItems.length > 0) {
            setAppliedInfo({ all: splittedItems.length, applied: newItems.length });
        }
        if (newItems.length > 0) {
            onChange?.([...value, ...newItems]);
        }
        setInputValue('');
    });
    const handleApplyValues = useCallbackRef((text) => applyItems(splitBySeparators(text, effectiveSeparators)));
    // The typed text goes to the list together with the picked suggestion.
    const handlePickSuggestion = useCallbackRef((option) => {
        setLockedSuggestionsQuery(suggestionsQuery);
        applyItems([...splitBySeparators(inputValue, effectiveSeparators), option.label]);
    });
    const handleSuggestForItem = useCallbackRef((item) => {
        setLockedSuggestionsQuery(item.label);
        setActiveSuggestionIndex(0);
        setAreSuggestionsDismissed(false);
        textareaRef.current?.focus();
    });
    const handleCopyValues = useCallbackRef(async () => navigator.clipboard.writeText(value.map((item) => item.label).join(',')));
    const handlePasteValues = useCallbackRef(async () => {
        let pastedText;
        try {
            pastedText = await navigator.clipboard.readText();
        }
        catch (err) {
            if (err instanceof Error && err.name === 'NotAllowedError') {
                notify.error({ title: placeholders.errorTitle, body: placeholders.errorAccessDenied });
            }
            else {
                notify.error({ title: placeholders.errorTitle, body: placeholders.errorAccessError });
            }
            return;
        }
        if (usesSeparatorSplit) {
            const splittedItems = splitBySeparators(pastedText, effectiveSeparators).map(formatSplitItem);
            const uniqueItems = Array.from(new Set(splittedItems));
            const newItems = uniqueItems.filter((item) => isValidItem(item)).map((item) => ({ label: item, value: item }));
            if (splittedItems.length > 0) {
                setPastedInfo({ all: splittedItems.length, pasted: newItems.length });
            }
            if (newItems.length > 0) {
                onChange?.([...value, ...newItems]);
            }
            return;
        }
        const splittedItems = pastedText
            .split(',')
            .map((s) => {
            const trimmedString = s.trim();
            return allowSpacing ? trimmedString : replaceSpaces(trimmedString);
        })
            .filter(Boolean);
        const uniqueItems = Array.from(new Set(splittedItems));
        const newItems = uniqueItems.filter((item) => isValidItem(item)).map((item) => ({ label: item, value: item }));
        if (splittedItems.length > 0) {
            setPastedInfo({ all: splittedItems.length, pasted: newItems.length });
        }
        if (newItems.length > 0) {
            onChange?.([...value, ...newItems]);
        }
    });
    const handleRemoveAll = useCallbackRef(() => onChange?.([]));
    const handleRemoveItem = useCallbackRef((itemToRemove) => {
        const newValue = value.filter((item) => item.value !== itemToRemove);
        onChange?.(newValue);
    });
    const handleCreateItem = useCallbackRef(() => {
        onChange?.([...value, { label: inputValue, value: inputValue }]);
        setInputValue('');
    });
    const handleClearInput = useDebouncedCallback(() => setInputValue(''), 100);
    const handleInputChange = useCallbackRef((e) => setInputValue(allowSpacing ? e.target.value : replaceSpaces(e.target.value)));
    const handleTextareaChange = useCallbackRef((e) => {
        setInputValue(allowSpacing ? e.target.value : replaceSpaces(e.target.value));
        setLockedSuggestionsQuery(undefined);
        setActiveSuggestionIndex(0);
        setAreSuggestionsDismissed(false);
    });
    const handleTextareaFocus = useCallbackRef(() => setAreSuggestionsDismissed(false));
    const handleTextareaKeyDown = useCallbackRef((e) => {
        // Keys pressed while an IME or a dead key is composing belong to the composition, not to the popover.
        if (!isSuggestionsOpen || e.nativeEvent.isComposing) {
            return;
        }
        if (e.key === 'Escape') {
            // Stopping the event keeps the popover's own dismiss from dropping its focus state, so typing reopens it.
            e.preventDefault();
            e.stopPropagation();
            setAreSuggestionsDismissed(true);
            return;
        }
        const optionsCount = visibleSuggestions.length;
        if (optionsCount === 0 || suggestions?.pending) {
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveSuggestionIndex((activeSuggestionOptionIndex + 1) % optionsCount);
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveSuggestionIndex((activeSuggestionOptionIndex - 1 + optionsCount) % optionsCount);
        }
        else if (e.key === 'Enter' && !e.shiftKey) {
            const activeOption = visibleSuggestions[activeSuggestionOptionIndex];
            if (activeOption) {
                e.preventDefault();
                handlePickSuggestion(activeOption);
            }
        }
    });
    const handleInputKeyDown = useCallbackRef((e) => {
        if (e.key === 'Enter') {
            if (!isEditMode) {
                return;
            }
            e.preventDefault();
            if (hasValueSeparators) {
                handleApplyValues(inputValue);
                return;
            }
            if (validationError) {
                notify.error({
                    title: placeholders.errorTitle ?? t('common:ERROR'),
                    body: validationError,
                });
                return;
            }
            handleCreateItem();
        }
    });
    const clearPastedInfo = useDebouncedCallback(() => setPastedInfo(undefined), 3000);
    const onMainTooltipVisibilityChange = useCallbackRef((visible) => visible && setPastedInfo(undefined));
    React.useEffect(() => {
        if (pastedInfo) {
            clearPastedInfo();
        }
    }, [clearPastedInfo, pastedInfo]);
    const clearAppliedInfo = useDebouncedCallback(() => setAppliedInfo(undefined), 3000);
    const onApplyTooltipVisibilityChange = useCallbackRef((visible) => visible && setAppliedInfo(undefined));
    React.useEffect(() => {
        if (appliedInfo) {
            clearAppliedInfo();
        }
    }, [appliedInfo, clearAppliedInfo]);
    const copyButton = (_jsx(CopyTooltip, { onClick: handleCopyValues, tooltip: placeholders.copy ?? '', "data-testid": dataTestId && `${dataTestId}-copy-tooltip`, copiedTooltip: placeholders.copied ?? '', placement: isTextareaMode ? 'top' : undefined, children: _jsx(Button, { "data-testid": dataTestId && `${dataTestId}-copy-button`, iconOnly: !isTextareaMode, secondary: !isTextareaMode, ghost: isTextareaMode, disabled: disabled || value.length === 0, children: _jsx(Icons.Copy, {}) }) }));
    const pasteButton = (_jsx(Tooltip, { "data-testid": dataTestId && `${dataTestId}-paste-tooltip`, disabled: disabled, placement: 'top', tooltip: placeholders.paste, trigger: 'hover', onVisibleChange: onMainTooltipVisibilityChange, children: _jsx(Tooltip, { "data-testid": dataTestId && `${dataTestId}-paste-info-tooltip`, placement: 'top', tooltip: `${placeholders.pasted} ${pastedInfo?.pasted} ${placeholders.totalSeparator} ${pastedInfo?.all}`, delayShow: 0, visible: !!pastedInfo, children: _jsx(Button, { "data-testid": dataTestId && `${dataTestId}-paste-button`, iconOnly: true, secondary: true, onClick: handlePasteValues, disabled: disabled, children: _jsx(Icons.Past, {}) }) }) }));
    const applyButton = (_jsx(Tooltip, { "data-testid": dataTestId && `${dataTestId}-apply-tooltip`, disabled: disabled || inputValue.trim().length === 0, placement: 'top', tooltip: placeholders.apply, trigger: 'hover', onVisibleChange: onApplyTooltipVisibilityChange, children: _jsx(Tooltip, { "data-testid": dataTestId && `${dataTestId}-apply-info-tooltip`, placement: 'top', tooltip: `${placeholders.applied} ${appliedInfo?.applied} ${placeholders.totalSeparator} ${appliedInfo?.all}`, delayShow: 0, visible: !!appliedInfo, children: _jsx(Button, { "data-testid": dataTestId && `${dataTestId}-apply-button`, iconOnly: true, primary: true, onClick: () => handleApplyValues(inputValue), disabled: disabled || inputValue.trim().length === 0, children: _jsx(ApplyIcon, {}) }) }) }));
    const editModeActions = (_jsxs(_Fragment, { children: [copyButton, pasteButton, actions] }));
    return (_jsxs(Wrapper, { "data-testid": dataTestId, children: [_jsx(InputWrapper, { children: isTextareaMode ? (_jsx(InputPopover, { open: isSuggestionsOpen, dataTestId: dataTestId && `${dataTestId}-suggestions`, content: suggestions && (_jsx(SuggestionsList, { queryLabel: suggestions.getQueryLabel(suggestionsQuery), emptyText: suggestions.emptyText, options: visibleSuggestions, activeIndex: activeSuggestionOptionIndex, pending: !!suggestions.pending, dataTestId: dataTestId, onPick: handlePickSuggestion, onActivate: setActiveSuggestionIndex })), children: _jsx(Textarea, { ref: mergedTextareaRef, dataTestId: dataTestId && `${dataTestId}-textarea`, placeholder: placeholders.input, onChange: handleTextareaChange, onKeyDown: handleTextareaKeyDown, onFocus: handleTextareaFocus, value: inputValue, disabled: disabled, rows: 4, actions: isEditMode ? (_jsxs(_Fragment, { children: [actions, applyButton] })) : undefined }) })) : (_jsxs(_Fragment, { children: [_jsx(Input, { ref: ref, "data-testid": dataTestId && `${dataTestId}-input`, placeholder: placeholders.input, icon: inputValue ? (_jsx(Icons.Clear, { filled: true, size: 20, color: colors.grey100_1, onClick: handleClearInput })) : (_jsx(Icons.Search, { size: 20, color: colors.grey100_1 })), onChange: handleInputChange, onKeyDown: handleInputKeyDown, onBlur: handleClearInput, isActionIcon: !!inputValue, value: inputValue, disabled: disabled }), isEditMode && editModeActions] })) }), _jsxs(ListWrapper, { $error: error, "data-testid": dataTestId && `${dataTestId}-list-wrapper`, children: [_jsxs(ListHeader, { children: [_jsxs(ListHeaderTitle, { children: [_jsx("span", { "data-testid": dataTestId && `${dataTestId}-selected-label`, children: placeholders.selected }), isTextareaMode && copyButton] }), isEditMode && (_jsx(Tooltip, { tooltip: placeholders.clearAll, disabled: disabled || value.length === 0, "data-testid": dataTestId && `${dataTestId}-clear-all-tooltip`, placement: 'top', children: _jsx(Button, { ghost: true, onClick: handleRemoveAll, "data-testid": dataTestId && `${dataTestId}-clear-all-button`, disabled: disabled || value.length === 0, children: _jsx(Icons.Clear, {}) }) }))] }), _jsxs(List, { "data-testid": dataTestId && `${dataTestId}-list`, $selectedLength: value.length, $minHeight: showCreatePreview || !!isLoading, children: [isLoading && (_jsx(ListItem, { "data-testid": dataTestId && `${dataTestId}-loading-item`, children: _jsx(Loader, { "data-testid": dataTestId && `${dataTestId}-loader`, height: 20, fullWidth: true }) })), showCreatePreview && (_jsx(ListItem, { "data-testid": dataTestId && `${dataTestId}-create-item`, $isPlaceholder: true, onClick: () => handleCreateItem(), children: _jsxs("span", { children: [placeholders.create, ` "${inputValue}"`] }) })), filteredAndSortedValues.map((item) => (_jsx(CreatableListItem, { item: item, formatOptionLabel: formatOptionLabel, disabled: disabled, isEditMode: isEditMode, dataTestId: dataTestId, onRemove: handleRemoveItem, onSelect: hasSuggestions ? handleSuggestForItem : undefined }, item.value)))] }), _jsxs(ListFooter, { children: [_jsx(SortOrderWrapper, { "data-testid": dataTestId && `${dataTestId}-sort-order`, onClick: handleSortOrderChange, children: sortOrder === SortOrder.ASC ? placeholders.sortOrderAsc : placeholders.sortOrderDesc }), _jsx(Total, { "data-testid": dataTestId && `${dataTestId}-total`, children: filteredAndSortedValues.length === value.length
                                    ? `${value.length} ${placeholders.total}`
                                    : `${filteredAndSortedValues.length} ${placeholders.totalSeparator} ${value.length}` })] })] })] }));
});
